import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import { ColumnIds } from '~/constants/table';

import type { InvoiceTemplateType } from './types';

import { paginatedApiResourceSchema } from '../common/schemas';
import { fetchJson } from '../fetch';
import { QueryKeys } from '../keys';
import { invoiceTemplateSchema } from './schemas';

export type InvoiceTemplateQueryMetadata = {
  filters: {
    brandId: number | null;
    clientId: number | null;
    search: string;
    templateType: InvoiceTemplateType | null;
  };
  pagination: { pageIndex: number; pageSize: number };
  sorting: { desc: boolean; id: string }[];
};

export const defaultInvoiceTemplateQueryMetadata = {
  pagination: { pageIndex: 0, pageSize: 10 },
  sorting: [{ id: ColumnIds.invoiceTemplatesTitle, desc: false }],
  filters: { search: '', brandId: null, clientId: null, templateType: null },
} satisfies InvoiceTemplateQueryMetadata;

export const isDefaultInvoiceTemplateQueryMetadata = (metadata: InvoiceTemplateQueryMetadata): boolean =>
  metadata.pagination.pageIndex === defaultInvoiceTemplateQueryMetadata.pagination.pageIndex &&
  metadata.pagination.pageSize === defaultInvoiceTemplateQueryMetadata.pagination.pageSize &&
  metadata.sorting.length === defaultInvoiceTemplateQueryMetadata.sorting.length &&
  metadata.sorting.every(
    (sorting, i) =>
      sorting.id === defaultInvoiceTemplateQueryMetadata.sorting[i].id &&
      sorting.desc === defaultInvoiceTemplateQueryMetadata.sorting[i].desc,
  ) &&
  metadata.filters.search === defaultInvoiceTemplateQueryMetadata.filters.search &&
  metadata.filters.brandId === defaultInvoiceTemplateQueryMetadata.filters.brandId &&
  metadata.filters.clientId === defaultInvoiceTemplateQueryMetadata.filters.clientId &&
  metadata.filters.templateType === defaultInvoiceTemplateQueryMetadata.filters.templateType;

export const invoiceTemplatesQueryOptions = (metadata: InvoiceTemplateQueryMetadata) =>
  queryOptions({
    queryKey: QueryKeys.invoiceTemplates.index.metadata(metadata),
    queryFn: async () => {
      const { filters, pagination, sorting } = metadata;

      const response = await fetchJson('/invoice-templates', {
        schema: paginatedApiResourceSchema(invoiceTemplateSchema.array()),
        params: {
          search: filters.search,
          clientId: [filters.clientId],
          brandId: [filters.brandId],
          templateType: filters.templateType,
          pagination: { pageIndex: pagination.pageIndex + 1, pageSize: pagination.pageSize },
          sorting,
        },
      });

      return response;
    },
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    gcTime: isDefaultInvoiceTemplateQueryMetadata(metadata) ? Infinity : undefined,
  });

export const useInvoiceTemplates = (metadata: InvoiceTemplateQueryMetadata) => {
  return useQuery(invoiceTemplatesQueryOptions(metadata));
};
